import React, { useContext } from 'react';
import gettext from '@eb/gettext';

import {
    EventCard,
    CONSUMER_CARD_DEFAULTS,
    transformDiscoveryEvent,
    DiscoveryEvent,
    useInfiniteEvents,
    getEventsById,
    FormattedEvent,
} from '@eb/event-renderer';
import { withMainControls } from '@eb/eds-structure';

import { UserContext, OrganizerContext, EnvContext } from '../context';
import { GA_ORGANIZER_CATEGORY } from '../constants';

import { ShowMoreEvents } from './ShowMoreEvents';

interface EventRendererProps {
    events: DiscoveryEvent[];
    eventMetaData: {
        num_past_events: number;
        has_next_past_page: boolean;
        num_future_events: number;
        has_next_future_page: boolean;
    };
    removeSaveHandlers?: boolean;
    type: string;
}

export interface EventCardEventData {
    id: string;
    isFree: boolean;
    imageUrl: string;
    name: string;
    isOnlineEvent: boolean;
    startTime: string;
    timezome: string;
    url: string;
    locationInfo: string;
    savedByYou: boolean;
    startDate?: string;
    subtitle?: string;
    formattedPriceString?: string;
}

interface ShowMoreEventsResponse {
    data: {
        events: DiscoveryEvent[];
        has_next_page: boolean;
    };
}

const fetchMoreOrganizerEvents = async (
    currentPage: number,
    id: string,
    eventType: string,
) => {
    const url = `/org/${id}/showmore/?type=${eventType}&page=${
        currentPage + 1
    }`;

    const response = await fetch(url);
    const responseData: ShowMoreEventsResponse = await response.json();
    let formattedEvents;

    if (eventType === 'past') {
        formattedEvents = responseData.data.events.map((event) =>
            transformDiscoveryEvent(event),
        );
    } else {
        const expandedEvents = await getEventsById(
            responseData.data.events.map(({ id }) => id),
        );
        formattedEvents = expandedEvents.events;
    }

    return {
        has_next_page: responseData.data.has_next_page,
        events: formattedEvents,
    };
};

export const EventRenderer: React.FunctionComponent<EventRendererProps> = (
    props,
) => {
    const currentPage = React.useRef(1);
    const { id } = useContext(OrganizerContext);
    const user = useContext(UserContext);
    const envContext = useContext(EnvContext);
    const currencyFormat = envContext.env?.currencyFormat;
    const isAuthenticated = user?.isAuthenticated;

    const { data, isFetchingMore, canFetchMore, fetchMore } = useInfiniteEvents(
        [props.type, 'organizer-events'],
        async () => {
            const organizerEvents = await fetchMoreOrganizerEvents(
                currentPage.current,
                id as string,
                props.type,
            );
            currentPage.current = currentPage.current + 1;

            return organizerEvents;
        },
        {
            getFetchMore: (page) => page.has_next_page,
            initialData: [
                {
                    events: props.events.map((event) =>
                        transformDiscoveryEvent(event),
                    ),
                    has_next_page:
                        props.type === 'past'
                            ? props.eventMetaData.has_next_past_page
                            : props.eventMetaData.has_next_future_page,
                },
            ],
            expandEventsOnMount: props.type !== 'past',
        },
    );

    if (!data || data[0].events.length === 0) {
        return (
            <div className="eds-text-bl">
                {gettext('Sorry, there are no upcoming events')}
            </div>
        );
    }

    return (
        <div>
            {data.map((response) =>
                response.events.map((eventData: FormattedEvent) => (
                    <EventCard
                        {...CONSUMER_CARD_DEFAULTS}
                        currencyFormat={currencyFormat || ''}
                        hideSaveButton={!!props.removeSaveHandlers}
                        key={eventData.id}
                        type="list"
                        isAuthenticated={!!isAuthenticated}
                        gaCategory={GA_ORGANIZER_CATEGORY}
                        user={{ id: user?.publicId || '' }}
                        eventData={eventData}
                    />
                )),
            )}
            <ShowMoreEvents
                isLoading={!!isFetchingMore}
                onClick={fetchMore}
                hasMore={canFetchMore}
            />
        </div>
    );
};

export const EventRenderWithControls = withMainControls(EventRenderer);
